import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

let refreshPromise = null;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const path = original?.url || "";
    const canRefresh = error.response?.status === 401 && original && !original._retried
      && !path.includes("/auth/login") && !path.includes("/auth/pin-login") && !path.includes("/auth/2fa/verify")
      && !path.includes("/auth/refresh") && !path.includes("/auth/logout");
    if (!canRefresh) throw error;

    original._retried = true;
    try {
      if (!refreshPromise) {
        refreshPromise = api.post("/auth/refresh").finally(() => { refreshPromise = null; });
      }
      await refreshPromise;
      return api(original);
    } catch {
      throw error;
    }
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Messaggio per gli errori di login: distingue "il server ha risposto no" (password sbagliata, blocco)
// da "la richiesta non è arrivata al backend" (rete, indirizzo vecchio, app installata su un dominio dismesso).
export function formatLoginError(error) {
  const detail = error?.response?.data?.detail;
  if (detail != null) return formatApiErrorDetail(detail);
  const host = typeof window !== "undefined" ? window.location.host : "";
  if (!error?.response) {
    return `Impossibile contattare il server (${host}). Controlla la connessione e riprova.`;
  }
  return `Il server non ha risposto come previsto (errore ${error.response.status} su ${host}). `
    + "Se usi l'app dalla schermata Home, rimuovila e reinstallala aprendo l'indirizzo attuale da Safari o Chrome.";
}

// File generati dal server (PDF, modelli Excel/CSV): li scarica l'app, che
// rinnova la sessione se serve. Aprire direttamente l'indirizzo del file
// manderebbe il cookie del browser, che può essere di una sessione chiusa
// ("Sessione revocata"), soprattutto dall'app installata.
const BLOB_URL_LIFETIME_MS = 60_000;

const fileNameFrom = (headers, fallback) => {
  const header = headers?.["content-disposition"] || "";
  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (encoded) { try { return decodeURIComponent(encoded[1]); } catch { /* nome di riserva */ } }
  return header.match(/filename="?([^";]+)"?/i)?.[1] || fallback;
};

// Blob.text() manca nei browser più vecchi: FileReader come alternativa.
const blobText = (blob) => blob.text ? blob.text() : new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result); reader.onerror = reject;
  reader.readAsText(blob);
});

const errorMessage = async (error, fallback) => {
  let detail = error.response?.data?.detail;
  if (!detail && error.response?.data instanceof Blob) {
    try { detail = JSON.parse(await blobText(error.response.data)).detail; } catch { detail = null; }
  }
  return detail ? formatApiErrorDetail(detail) : fallback;
};

// Apre un file del server (path relativo ad /api) in una nuova scheda.
// `win`: scheda già aperta dal chiamante (va aperta in modo sincrono al clic,
// prima di qualunque attesa, per evitare il blocco popup di Safari).
// In caso di errore mostra l'avviso e restituisce il messaggio (null se è andato bene).
export async function openServerPdf(path, params, win = window.open("", "_blank")) {
  try {
    const { data } = await api.get(path, { params, responseType: "blob" });
    const url = URL.createObjectURL(data);
    if (win) win.location.href = url; else window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_LIFETIME_MS);
    return null;
  } catch (error) {
    if (win) win.close();
    const message = await errorMessage(error, "Impossibile aprire il PDF. Riprova.");
    toast.error(message);
    return message;
  }
}

// Scarica un file del server (modelli di importazione) col suo nome.
export async function downloadServerFile(path, params, fallbackName) {
  try {
    const { data, headers } = await api.get(path, { params, responseType: "blob" });
    const url = URL.createObjectURL(data);
    const link = Object.assign(document.createElement("a"), { href: url, download: fileNameFrom(headers, fallbackName) });
    document.body.appendChild(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_LIFETIME_MS);
    return null;
  } catch (error) {
    const message = await errorMessage(error, "Impossibile scaricare il file. Riprova.");
    toast.error(message);
    return message;
  }
}

export const BRAND = {
  name: "DF HACCP",
  parent: "Dottor Dario Feliciano",
  parentTitle: "Biologo – Consulente Esperto in Sicurezza Alimentare",
  logo: "/df-haccp-logo.jpg",
  symbol: "/df-haccp-simbolo.png",
  loginImage: "/dario-feliciano-haccp.jpg",
  whatsapp: "+393342614428",
  phone: "+393342614428",
  phoneDisplay: "334 261 4428",
  website: "https://www.df-consulting-group.it",
  websiteDisplay: "www.df-consulting-group.it",
};

export default api;
